import type { components } from '@/generated/contracts/api-v1';
import { fieldMap, requireId, type FieldSpec, type LegacyRow } from './fieldMap';

// ui_element_types table; LibraryMapping.ToDto(UiElementType) in lureo-backend Data/Api/V1/Library/LibraryDtos.cs
export type UiElementTypeDto = components['schemas']['UiElementTypeDto'];
export type CreateUiElementTypeRequest = components['schemas']['CreateUiElementTypeRequest'];

const spec = {
  uniqueId: 'type_id',
  createdAt: 'created_at',
  // The owning membership; null for Lureo's own
  orgMemberId: 'org_member_id',
  sourceName: 'source_name',
  category: 'category',
  displayName: 'display_name',
  textBody: 'text_body',
  maxCharacterCount: 'max_character_count',
  styleCornerRadius: 'style_corner_radius',
  styleBackgroundColor: 'style_background_color',
  styleFont: 'style_font',
  styleTextSize: 'style_text_size',
  styleTextColour: 'style_text_colour',
  styleBorder: 'style_border',
  styleBorderRadius: 'style_border_radius',
  styleBorderThickness: 'style_border_thickness',
  styleBorderColour: 'style_border_colour',
  styleAlignment: 'style_alignment',
  styleJustify: 'style_justify',
  availability: 'availability',
  isActive: 'is_active',
  // The entity holds uss as an object, not JSON text
  uss: { legacy: 'uss', as: 'json' },
  beginTime: 'begin_time',
  endTime: 'end_time',
} as const satisfies FieldSpec<UiElementTypeDto>;

const map = fieldMap<UiElementTypeDto, typeof spec>('UI element type', spec);

export type LegacyUiElementType = LegacyRow<typeof spec, UiElementTypeDto>;

/** updateUiElementType accepts these. */
export const UI_ELEMENT_TYPE_PATCH_FIELDS = [
  'sourceName', 'category', 'displayName', 'textBody', 'maxCharacterCount', 'styleCornerRadius', 'styleBackgroundColor',
  'styleFont', 'styleTextSize', 'styleTextColour', 'styleBorder', 'styleBorderRadius', 'styleBorderThickness',
  'styleBorderColour', 'styleAlignment', 'styleJustify', 'availability', 'isActive', 'uss', 'beginTime', 'endTime',
] as const;
const CREATE_FIELDS = ['uniqueId', ...UI_ELEMENT_TYPE_PATCH_FIELDS] as const;

export const uiElementTypeToLegacy = (dto: UiElementTypeDto): LegacyUiElementType => map.toLegacy(dto);

/** createUiElementType body from a legacy insert row; type_id is required. The owner is the caller's membership. */
export function uiElementTypeCreateBody(row: Partial<LegacyUiElementType>): CreateUiElementTypeRequest {
  const body = map.toBody(row, CREATE_FIELDS, ['org_member_id', 'created_at']);
  return { ...body, uniqueId: requireId('UI element type', 'type_id', body.uniqueId) };
}

/** updateUiElementType body from a legacy partial update. */
export const uiElementTypePatchBody = (changes: Partial<LegacyUiElementType>) => map.toBody(changes, UI_ELEMENT_TYPE_PATCH_FIELDS);
